import { HelpCircle, LoaderCircle } from "lucide-react";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import CustomToggleSelect from "@/components/ui/CustomToggleSelect";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import React from "react";
//import { DEFAULT_EXERCISES } from "@/lib/options/defaultExerciseOptions";
import { useTrackedExercises } from "@/hooks/useTrackedExercises";
import { useToastHandler } from "@/hooks/useToastHandler";

interface ManualEntryFormData {
  exerciseNanoId: string;
  weight: number;
  unit: "lb" | "kg";
  reps: number;
}

interface FormProps {
  formData: ManualEntryFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleExerciseChange: (id: number | string, label: string) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setShowTrainingLoadModal: (show: boolean) => void;
  isSubmitting: boolean;
  validationErrors: { [key: string]: string[] };
  removeValidationError: (key: string) => void;
}

const Form: React.FC<FormProps> = ({
  formData,
  handleInputChange,
  handleExerciseChange,
  handleSubmit,
  setShowTrainingLoadModal,
  isSubmitting,
  validationErrors,
  removeValidationError,
}) => {
  const { handleError } = useToastHandler();
  const { data: trackedExercises, isLoading, isError } = useTrackedExercises();


  // reactive function to get effective exercises
  // function useEffectiveExercises() {
  //   const effectiveExercises =
  //     trackedExercises && trackedExercises.length > 0
  //       ? trackedExercises
  //       : DEFAULT_EXERCISES;
  //   return effectiveExercises;
  // }

  // Prepare options for combobox, using default exercises if none available
  const exerciseOptions: ComboboxOption[] =
    trackedExercises?.map((exercise) => ({
      id: exercise.nano_id,
      label: exercise.name,
    })) || [];

  // make sure to process the error once
  if (isError) {
    handleError(
      "Failed to load exercises. You can manually type an exercise name to track the set.",
      3000
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 ">
      {/* Exercise Selection */}
      <div>
        <label
          htmlFor="exerciseNanoId"
          className="block mb-2 text-sm font-medium text-tx-dark"
        >
          Exercise
        </label>
        {isLoading ? (
          <div className="form-default-size w-full p-3 text-tx-muted flex items-center gap-2">
            <LoaderCircle className="animate-spin w-5 h-5" />
            Loading exercises...
          </div>
        ) : (
          <div>
            <Combobox
              options={exerciseOptions}
              value={formData.exerciseNanoId}
              onChange={(id, label) => {
                handleExerciseChange(id, label);
                // Clear validation errors when user selects exercise
                removeValidationError('exercise_nano_id');
                removeValidationError('exercise_name');
              }}
              onCreateOption={(newExerciseName) => {
                const tempId = `new-${Date.now()}`;
                handleExerciseChange(tempId, newExerciseName);
                // Clear validation errors when user creates new exercise
                removeValidationError('exercise_nano_id');
                removeValidationError('exercise_name');
              }}
              placeholder={
                exerciseOptions.length > 0
                  ? "Select or type to create new"
                  : "E.g.: Squat"
              }
            />
            {(validationErrors.exercise_nano_id ||
              validationErrors.exercise_name) && (
              <div className="mt-1">
                {validationErrors.exercise_nano_id?.map((error, index) => (
                  <p key={`id-${index}`} className="text-sm text-tx-error">
                    {error}
                  </p>
                ))}
                {validationErrors.exercise_name?.map((error, index) => (
                  <p key={`name-${index}`} className="text-sm text-tx-error">
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Weight and Unit */}
      <div className="flex gap-4">
        <div className="flex-1">
          <label
            htmlFor="weight"
            className="mb-2 text-sm font-medium flex items-center text-tx-dark"
          >
            Weight
            <div
              className="ml-1 cursor-pointer"
              onClick={() => setShowTrainingLoadModal(true)}
            >
              <HelpCircle size={14} />
            </div>
          </label>
          <Input
            type="number"
            id="weight"
            name="weight"
            min="0"
            step="0.1"
            value={formData.weight === 0 ? "" : formData.weight}
            onChange={(e) => {
              handleInputChange(e);
              // Clear validation errors when user starts typing
              removeValidationError('weight');
            }}
            className={`w-full p-3 rounded-lg text-tx-dark border border-bd-light ${
              validationErrors.weight ? "border-red-500" : ""
            }`}
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            placeholder="E.g.: 50"
            onFocus={(e) => e.target.select()}
            required
          />
          {validationErrors.weight && (
            <div className="mt-1">
              {validationErrors.weight.map((error, index) => (
                <p key={index} className="text-sm text-tx-error">
                  {error}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Unit */}
        <div className="flex-1">
          <label
            htmlFor="unit"
            className="block mb-2 text-sm font-medium text-tx-dark"
          >
            Unit
          </label>
          <CustomToggleSelect
            value={formData.unit}
            className="w-full"
            setValue={(unit) =>
              handleInputChange({
                target: { name: "unit", value: unit },
              } as unknown as React.ChangeEvent<HTMLInputElement>)
            }
            options={[
              { label: "lb", value: "lb" },
              { label: "kg", value: "kg" },
            ]}
          />
        </div>
      </div>
      {/* Reps Slider */}
      <div className="flex flex-col gap-2 pb-6">
        <div className="flex justify-between mb-2">
          <label htmlFor="reps" className="text-sm font-medium text-tx-dark">
            Reps (repetitions)
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={formData.reps}
            onChange={(e) => {
              const value = Math.min(
                100,
                Math.max(1, Number(e.target.value) || 1)
              );
              handleInputChange({
                target: { name: "reps", value },
              } as unknown as React.ChangeEvent<HTMLInputElement>);
              // Clear validation errors when user starts typing
              removeValidationError('reps');
            }}
            className={`text-center pl-2 py-0 text-sm font-medium text-tx-primary border-b ${
              validationErrors.reps ? "border-red-500" : "border-bd-light"
            }`}
            style={{
              background: "transparent",
              outline: "none",
            }}
            onFocus={(e) => e.target.select()}
            aria-label="Edit reps value"
          />
        </div>
        <Slider
          min={1}
          max={20}
          step={1}
          value={[formData.reps > 20 ? 20 : formData.reps]}
          onValueChange={([value]) => {
            handleInputChange({
              target: { name: "reps", value },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
            // Clear validation errors when user interacts with slider
            removeValidationError('reps');
          }}
          className="w-full"
        />
        <div className="flex justify-between mt-1 text-xs text-tx-muted">
          <span>1</span>
          <span>10</span>
          <span>20+</span>
        </div>
        {validationErrors.reps && (
          <div className="mt-1">
            {validationErrors.reps.map((error, index) => (
              <p key={index} className="text-sm text-tx-error">
                {error}
              </p>
            ))}
          </div>
        )}
      </div>
      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Saving..." : "Save the Set"}
      </Button>
    </form>
  );
};

export default Form;
